<!-- 
A listing of PRISMS users with LTPS roles.
-->
<template>
  <div>
    <v-card :class="embedded ? 'v-card--flat' : 'home'">
      <our-table-header 
        :listing="listing"
        :search-filters="searchFilters"
        :criteria-mutation="criteriaMutation"
        :has-export="hasExport"
        @click:refresh="refresh" 
        @click:export="exportToExcel" 
        @update:pageNumber="updatePageNumber" 
        @update:criteria="refresh"
      />
      <our-table-busy-indicator :show="listing.loading"/>
      <v-data-table 
        :headers="headers" 
        :items="listing.data" 
        :pagination="listing.paging" 
        :total-items="listing.paging.totalItems" 
        :rows-per-page-items="rowsPerPageItems" 
        :no-data-text="noDataText"
        must-sort 
        hide-actions
        @update:pagination="updatePagination"
      >
        <template slot="items" slot-scope="props">
          <tr @click.stop="open(props.item.id)">
            <our-nav-col :row="props.item.name"/>
            <td>{{ props.item | name }}</td>
            <td>{{ props.item.emailAddress }}</td>
            <td>{{ props.item.lastLogonDate | compactDateTime }} {{ props.item.failedAttempts | failedAttempts }}</td>
            <td>{{ lookups.userStatusDescriptions[props.item.status] }}</td>
            <td>{{ props.item.passwordExpiryDate | compactDate }}</td>
            <td>{{ props.item.roles | roles }}</td>
            <td v-if="testingToolsEnabled && showActions" class="text-xs-right px-2">
              <v-tooltip bottom>
                <v-btn 
                  slot="activator" icon small class="ma-0" 
                  @click.stop="edit(props.item.id)"
                >
                  <v-icon small>edit</v-icon>
                </v-btn>
                <span>Click to edit this user's details</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-btn 
                  slot="activator" icon small class="ma-0" 
                  @click.stop="signInAs(props.item.emailAddress)"
                >
                  <v-icon small>person</v-icon>
                </v-btn>
                <span>Click to impersonate this user</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <our-table-footer :paging="listing.paging" @update:pageNumber="updatePageNumber" @update:rowsPerPage="updateRowsPerPage" />
    </v-card>
    <prisms-user-edit-dialog v-model="showEditDialog" :user-id="userId" />
  </div>
</template>

<script>
import PrismsUserEditDialog from "@/components/prisms-users/PrismsUserEditDialog.vue";
import tableMixins from "@/mixins/table.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as action from "@/store/prisms-users/action-types";
import * as mutation from "@/store/prisms-users/mutation-types";
import { getSearchFilters } from "@/store/prisms-users/helpers";
import { SIGN_OUT } from "@/store/users/action-types";
import { SIGN_IN_SET_DATA } from "@/store/users/mutation-types";
import { USERS_EXPORT } from "@/store/claim-types";

export default {
  components: { PrismsUserEditDialog },
  filters: {
    name: user => `${user.familyName}, ${user.firstName}`,
    roles: r => (r ? r.join(", ") : ""),
    failedAttempts: attempts => (attempts ? `(${attempts} failed attempt${attempts > 1 ? "s" : ""} since then)` : "")
  },
  mixins: [tableMixins, filterMixins, lookupMixins],
  props: {
    showActions: {
      type: Boolean,
      default: false
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      action: action.LIST,
      exportAction: action.EXPORT,
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      pagingMutation: mutation.LIST_SET_PAGING,
      userId: null,
      showEditDialog: false
    };
  },
  computed: {
    headers() {
      const h = [
        { text: "", value: "Id", class: "navCol", width: 0, sortable: false },
        { text: "Name", value: "FamilyName,FirstName", sortable: this.canSort },
        { text: "Email", value: "EmailAddress", sortable: this.canSort },
        { text: "Last logon", value: "LastLogonDate", sortable: this.canSort },
        { text: "Status", value: "Status", sortable: this.canSort },
        { text: "Password expires", value: "PasswordExpiryDate", sortable: this.canSort },
        { text: "Role(s)", sortable: false }
      ];
      if (this.testingToolsEnabled && this.showActions) {
        h.push({ text: "Actions", value: "Id", sortable: false, align: "right", width: 70 });
      }
      return h;
    },
    listing() {
      return this.$store.state.prismsUsers.listing;
    },
    searchFilters() {
      return getSearchFilters();
    },
    criteria() {
      return this.listing.criteria;
    },
    loaded() {
      return this.listing.loaded;
    },
    testingToolsEnabled() {
      return this.$store.getters.testingToolsEnabled;
    },
    isSignedIn() {
      return this.$store.getters.isSignedIn;
    },
    hasExport() {
      return this.$store.getters.claims.includes(USERS_EXPORT);
    }
  },
  watch: {
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      if (!this.listing.loaded && !this.listing.loading) {
        this.populate();
      }
    },
    edit(userId) {
      if (this.testingToolsEnabled) {
        this.userId = userId;
        this.showEditDialog = true;
      }
    },
    open(userId) {
      if (this.showActions) {
        this.edit(userId);
      } else {
        this.$router.push(`/users/${userId}`);
      }
    },
    async signInAs(emailAddress) {
      this.$store.commit(SIGN_IN_SET_DATA, { email: emailAddress, password: "password" });
      if (this.isSignedIn) {
        await this.$store.dispatch(SIGN_OUT);
      }
      this.$router.push("/sign-in");
    }
  }
};
</script>
